import cn from 'classnames';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';

import { ButtonV3 } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import Image from 'components/Image';
import { useResponsive } from 'hooks/useResponsive';
import { useAuth } from 'lib/providers/AuthProvider';

import { CHROME_EXTENSION_STORE_LINK } from 'utils/urls';
import { CVUploadModalInstance } from '../CVUploadModal';

import styles from './ProfileWelcomePopup.module.scss';

export interface ProfileWelcomePopupProps {
  onClose: () => void;
}

export const ProfileWelcomePopup: React.FC<ProfileWelcomePopupProps> = ({
  onClose,
}) => {
  const [t] = useTranslation('candidate.profile');
  const screens = useResponsive();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { candidate } = useAuth();

  const { resume_url } = candidate || {};

  const stepsData = useMemo(() => {
    const steps = [
      {
        title: t('profile-welcome-popup.step1.title'),
        imageSrc: '/images/profile_welcome_step1.png',
        stepInfo: <>{t('profile-welcome-popup.step1.desc')}</>,
        isCVUploadStep: false,
      },
      {
        title: t('profile-welcome-popup.step2.title'),
        imageSrc: '/images/profile_welcome_step2.png',
        stepInfo: <>{t('profile-welcome-popup.step2.desc')}</>,
        isCVUploadStep: false,
      },
      {
        title: t('profile-welcome-popup.step3.title'),
        imageSrc: '/images/profile_welcome_step3.png',
        stepInfo: (
          <Trans t={t} i18nKey="profile-welcome-popup.step3.desc">
            A
            <a
              href={CHROME_EXTENSION_STORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              B
            </a>
          </Trans>
        ),
        isCVUploadStep: false,
      },
      {
        title: t('profile-welcome-popup.step4.title'),
        imageSrc: '/images/profile_welcome_step4.png',
        stepInfo: <>{t('profile-welcome-popup.step4.desc')}</>,
        isCVUploadStep: false,
      },
    ];
    if (!resume_url) {
      steps.push({
        title: t('profile-welcome-popup.step5.title'),
        imageSrc: '/images/profile_welcome_step5.png',
        stepInfo: <>{t('profile-welcome-popup.step5.desc')}</>,
        isCVUploadStep: true,
      });
    }
    return steps;
  }, []);

  const changeStep = (step: number) => {
    if (step > stepsData.length - 1) {
      onClose();
    } else {
      setCurrentIndex(step);
    }
  };

  const handleCVUpload = async () => {
    try {
      onClose();
      await CVUploadModalInstance();
    } catch (error) {
      console.error(error);
    }
  };

  const currentStep = stepsData[currentIndex];

  return (
    <div className={styles['welcome-popup']}>
      <div className={styles['popup-screen']}>
        <div className={styles['popup-screen-header']}>
          <p>{currentStep.title}</p>
          <IconButton
            iconName="close"
            size="small"
            className={styles['close-icon']}
            onClick={onClose}
          />
        </div>
        <div className={styles['popup-screen-content']}>
          <Image
            src={currentStep.imageSrc}
            height={screens.sm ? 137 : 180}
            className={styles.image}
          />
          <p className={styles['steps-info']}>{currentStep.stepInfo}</p>
          {!currentStep.isCVUploadStep && (
            <div className={styles['steps-indicator']}>
              {Array.from(
                Array(stepsData.filter((step) => !step.isCVUploadStep).length)
              ).map((_, index) => (
                <div
                  key={index}
                  className={cn(styles['steps-indicator-step'], {
                    [styles.active]: index === currentIndex,
                  })}
                ></div>
              ))}
            </div>
          )}
        </div>
        <div className={styles['popup-screen-action']}>
          {currentIndex !== 0 && (
            <ButtonV3
              color="secondary"
              isFullWidth
              onClick={() =>
                currentStep.isCVUploadStep
                  ? onClose()
                  : changeStep(currentIndex - 1)
              }
            >
              {currentStep.isCVUploadStep
                ? t('profile-welcome-popup.button.continue-without-cv')
                : t('profile-welcome-popup.button.previous')}
            </ButtonV3>
          )}
          <ButtonV3
            isFullWidth
            onClick={() =>
              currentStep.isCVUploadStep
                ? handleCVUpload()
                : changeStep(currentIndex + 1)
            }
          >
            {currentStep.isCVUploadStep
              ? t('profile-welcome-popup.button.upload-cv')
              : t('profile-welcome-popup.button.next')}
          </ButtonV3>
        </div>
      </div>
    </div>
  );
};
